import React, { createContext, useContext, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import RequestedActionError from "../errors/RequestedActionError";
import "./css/ErrorModal.css";

// Create the context
const ErrorContext = createContext();

// Global error handler instance (singleton pattern)
let globalErrorHandler = null;

export const setGlobalErrorHandler = (handler) => {
  globalErrorHandler = handler;
};

export const showGlobalError = async (statusCode, message, actionName) => {
  if (globalErrorHandler) {
    return await globalErrorHandler.showError(statusCode, message, actionName);
  } else {
    console.error("Global error handler not initialized");
    throw new Error(message); // Fallback to throwing the error
  }
};

// Custom hook to use the error context
export const useErrorHandler = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error("useErrorHandler must be used within an ErrorProvider");
  }
  return context;
};

// Error provider component
export const ErrorProvider = ({ children }) => {
  const [errorState, setErrorState] = useState(null);

  const showError = (
    statusCode,
    message,
    actionName = "perform this action"
  ) => {
    return new Promise((resolve) => {
      setErrorState({
        statusCode,
        message,
        actionName,
        onClose: () => {
          setErrorState(null);
          resolve(); // Resolve the promise when user clicks OK/Close
        },
      });
    });
  };

  const hideError = () => {
    setErrorState(null);
  };

  // Register this ErrorProvider as the global error handler when it mounts
  useEffect(() => {
    const handler = { showError, hideError };
    setGlobalErrorHandler(handler);

    // Cleanup on unmount
    return () => {
      setGlobalErrorHandler(null);
    };
  }, []);

  const value = {
    showError,
    hideError,
    errorState,
  };

  return (
    <ErrorContext.Provider value={value}>
      {children}
      {errorState &&
        createPortal(
          <div className="error-modal-overlay">
            <div className="error-modal-content">
              <RequestedActionError
                statusCode={errorState.statusCode}
                message={errorState.message}
                actionName={errorState.actionName}
                onClose={errorState.onClose}
                onRetry={null} // No retry in global error handler
              />
            </div>
          </div>,
          document.body
        )}
    </ErrorContext.Provider>
  );
};
